import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyVenueProfile, updateMyVenueProfile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Save, X, Plus as PlusIcon, AlertCircle, CheckCircle2, FileText, Speaker } from 'lucide-react';
import { toast } from 'sonner';
import { cn, validatePhone } from '../lib/utils';
import { RiderUpload } from '../components/RiderUpload';
import { VenuePhotoUpload } from '../components/VenuePhotoUpload';
import VenueSwapConfirmations from '../components/VenueSwapConfirmations';
import { useAuth } from '../context/AuthContext';
import {
  FOOD_SOURCE_OPTIONS, DIET_OPTIONS, DRESSING_ROOM_OPTIONS, GREEN_ROOM_OPTIONS,
  ARTIST_TOILET_OPTIONS, SHOWER_OPTIONS, STORAGE_OPTIONS, WIFI_OPTIONS, DRINK_TOKEN_OPTIONS,
  EMPTY_FACILITIES_FORM, facilitiesToForm, formToFacilities,
} from '../lib/facilities';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

// Radix Select can't hold an empty value, so "Not set" gets a stand-in.
const UNSET = '__unset';

function FacilitySelect({ id, label, value, onChange, options, disabled }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground">{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v === UNSET ? '' : v)} disabled={disabled}>
        <SelectTrigger id={id} className="h-12 bg-secondary border-border" data-testid={`${id}-select`}>
          <SelectValue placeholder="Not set" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNSET}>Not set</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function MyVenueProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Basic — venue identity
  const [venueName, setVenueName] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('NL');
  const [phone, setPhone] = useState('');
  const [shortBio, setShortBio] = useState('');
  const [visibilityPublic, setVisibilityPublic] = useState(true);
  const [venueAddress, setVenueAddress] = useState('');
  const [publicTransitAccess, setPublicTransitAccess] = useState('good');

  // Equipment — venue constants
  const [venueProvides, setVenueProvides] = useState([]);
  const [talentBrings, setTalentBrings] = useState([]);

  // Facilities — set once, copied into each new gig
  const [facilities, setFacilities] = useState(EMPTY_FACILITIES_FORM);
  const setFacility = (key) => (value) => setFacilities((f) => ({ ...f, [key]: value }));

  // Tag inputs
  const [newVenueProvides, setNewVenueProvides] = useState('');
  const [newTalentBrings, setNewTalentBrings] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyVenueProfile();
      const p = response.data;
      setProfile(p);
      setVenueName(p.venueName || '');
      setContactPersonName(p.contactPersonName || '');
      setCity(p.city || '');
      setCountry(p.country || 'NL');
      setPhone(p.phone || '');
      setShortBio(p.shortBio || '');
      setVisibilityPublic(p.visibilityPublic !== false);
      setVenueAddress(p.venueAddress || '');
      setPublicTransitAccess(p.publicTransitAccess || 'good');
      setVenueProvides(p.venueProvides || []);
      setTalentBrings(p.talentBrings || []);
      setFacilities(facilitiesToForm(p.facilities));
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!venueName || !contactPersonName || !city || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const phoneError = validatePhone(phone);
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setSaving(true);
    try {
      await updateMyVenueProfile({
        venueName,
        contactPersonName,
        city,
        country,
        phone,
        shortBio,
        visibilityPublic,
        venueAddress: venueAddress || null,
        publicTransitAccess: publicTransitAccess || null,
        venueProvides,
        talentBrings,
        facilities: formToFacilities(facilities),
      });
      toast.success('Profile saved!');
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addTag = (value, list, setList, setInput, max) => {
    if (!value.trim()) return;
    if (list.length >= max) {
      toast.error(`Maximum ${max} items allowed`);
      return;
    }
    if (!list.includes(value.trim())) {
      setList([...list, value.trim()]);
    }
    setInput('');
  };

  const removeTag = (value, list, setList) => {
    setList(list.filter((v) => v !== value));
  };

  if (loading) {
    return (
      <div className="content-container pb-32 md:pb-8">
        <PageSkeleton label="Loading venue profile">
          <div className="flex items-start justify-between gap-4">
            <div><Bone w={150} h={30} tone="card" /><Bone w={120} h={30} tone="card" className="mt-2" /></div>
            <Bone w={145} h={40} r="md" tone="amber" className="mt-4" />
          </div>
          <Bone w={90} h={22} r="pill" className="mt-3" />
          <BoneCard className="p-6 mt-6">
            <Bone w="62%" h={28} />
            <Bone w="56%" h={14} className="mt-5" />
          </BoneCard>
          <BoneCard className="p-6 mt-6">
            <Bone w={150} h={20} />
            {[0, 1, 2].map((f) => (
              <div key={f} className="mt-5"><Bone w={100} h={12} /><Bone h={46} r="md" className="mt-2" /></div>
            ))}
          </BoneCard>
        </PageSkeleton>
      </div>
    );
  }

  const isComplete = profile?.isComplete;

  const REQUIRED_FIELDS = [
    { key: 'venueName', label: 'Venue name', value: venueName },
    { key: 'contactPersonName', label: 'Contact person', value: contactPersonName },
    { key: 'phone', label: 'Phone', value: phone },
    { key: 'city', label: 'City', value: city },
  ];
  const missingFields = REQUIRED_FIELDS.filter(f => !f.value);

  return (
    <div className="content-container pb-32 md:pb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title gold-accent">VENUE PROFILE</h1>
          <div className="flex items-center gap-2 mt-2">
            {isComplete ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            ) : (
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">
                <AlertCircle className="h-3 w-3 mr-1" />
                Incomplete
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/venues/requests/new">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-request-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </Button>
          </Link>
        </div>
      </div>

      {!isComplete && missingFields.length > 0 && (
        <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm">
          <p className="text-orange-400 font-medium mb-1.5">Complete your profile to create requests</p>
          <p className="text-orange-400/70">Missing: {missingFields.map(f => f.label).join(', ')}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Pending rider swap confirmations (Rule 3) */}
        <VenueSwapConfirmations />

        {/* Basic Info */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="venueName" className="text-foreground">Venue Name *</Label>
                <Input
                  id="venueName"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="Your venue name"
                  className="h-12 bg-secondary border-border"
                  data-testid="venue-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPersonName" className="text-foreground">Contact Person *</Label>
                <Input
                  id="contactPersonName"
                  value={contactPersonName}
                  onChange={(e) => setContactPersonName(e.target.value)}
                  placeholder="Primary contact name"
                  className="h-12 bg-secondary border-border"
                  data-testid="contact-person-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground">Phone (WhatsApp) *</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+31612345678"
                  className="h-12 bg-secondary border-border"
                  data-testid="phone-input"
                />
                <p className="text-xs text-muted-foreground">Include country code, e.g. +31612345678</p>
                {phone && validatePhone(phone) && (
                  <p className="text-xs text-red-400">{validatePhone(phone)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="text-foreground">City *</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Amsterdam"
                  className="h-12 bg-secondary border-border"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country" className="text-foreground">Country</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="h-12 bg-secondary border-border" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NL">Netherlands</SelectItem>
                    <SelectItem value="BE">Belgium</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="venueAddress" className="text-foreground">Full Address</Label>
              <Input
                id="venueAddress"
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
                placeholder="e.g., Leidseplein 12, 1017 PT Amsterdam"
                className="h-12 bg-secondary border-border"
                data-testid="venue-address-input"
              />
              <p className="text-xs text-muted-foreground">Used by talent to calculate travel time</p>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Public Transit Access</Label>
              <Select value={publicTransitAccess} onValueChange={setPublicTransitAccess}>
                <SelectTrigger className="h-12 bg-secondary border-border" data-testid="transit-access-select">
                  <SelectValue placeholder="Select accessibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good (near metro/tram/bus)</SelectItem>
                  <SelectItem value="limited">Limited (some transit options)</SelectItem>
                  <SelectItem value="poor">Poor (car recommended)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Helps talent plan their journey</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shortBio" className="text-foreground">About the Venue</Label>
              <Textarea
                id="shortBio"
                value={shortBio}
                onChange={(e) => setShortBio(e.target.value)}
                placeholder="Tell talent about your venue..."
                maxLength={500}
                rows={3}
                className="bg-secondary border-border"
                data-testid="short-bio-input"
              />
              <p className="text-xs text-muted-foreground">{shortBio.length}/500</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg border border-border">
              <div>
                <Label className="text-foreground">Public Visibility</Label>
                <p className="text-xs text-muted-foreground">Show in directory when profile is complete</p>
              </div>
              <Switch
                checked={visibilityPublic}
                onCheckedChange={setVisibilityPublic}
                data-testid="visibility-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Venue Photo: saved the moment it is picked, apart from the Save button below. */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Venue Photo</CardTitle>
          </CardHeader>
          <CardContent>
            <VenuePhotoUpload photoUrl={profile?.photoUrl} onSaved={setProfile} />
          </CardContent>
        </Card>

        {/* Equipment */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Equipment</CardTitle>
            <p className="text-sm text-muted-foreground">
              What your venue has available — these will be pre-filled on new requests
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Venue Provides (max 10)</Label>
              <div className="flex gap-2">
                <Input
                  value={newVenueProvides}
                  onChange={(e) => setNewVenueProvides(e.target.value)}
                  placeholder="e.g., PA System, Monitor wedges"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    addTag(newVenueProvides, venueProvides, setVenueProvides, setNewVenueProvides, 10)
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() =>
                    addTag(newVenueProvides, venueProvides, setVenueProvides, setNewVenueProvides, 10)
                  }
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {venueProvides.map((item) => (
                  <Badge key={item} variant="secondary" className="gap-1 bg-secondary border-border">
                    {item}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeTag(item, venueProvides, setVenueProvides)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Talent Should Bring (max 10)</Label>
              <div className="flex gap-2">
                <Input
                  value={newTalentBrings}
                  onChange={(e) => setNewTalentBrings(e.target.value)}
                  placeholder="e.g., Own instrument, In-ear monitors"
                  className="h-10 bg-secondary border-border"
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    addTag(newTalentBrings, talentBrings, setTalentBrings, setNewTalentBrings, 10)
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-border bg-secondary"
                  onClick={() =>
                    addTag(newTalentBrings, talentBrings, setTalentBrings, setNewTalentBrings, 10)
                  }
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {talentBrings.map((item) => (
                  <Badge key={item} variant="outline" className="gap-1 border-border">
                    {item}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeTag(item, talentBrings, setTalentBrings)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Facilities */}
        <Card className="bg-card border-border" data-testid="facilities-section">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Facilities</CardTitle>
            <p className="text-sm text-muted-foreground">
              Set these once. Every new request starts with them, and you can still change
              them for a single gig.
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Food */}
            <div className="space-y-4">
              <p className="micro-label">Food</p>
              <FacilitySelect
                id="food-source"
                label="Where the food comes from"
                value={facilities.foodSource}
                onChange={setFacility('foodSource')}
                options={FOOD_SOURCE_OPTIONS}
              />
              {facilities.foodSource === 'nearby' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nearby-note" className="text-foreground">Food Nearby</Label>
                    <Input
                      id="nearby-note"
                      value={facilities.nearbyNote}
                      onChange={(e) => setFacility('nearbyNote')(e.target.value)}
                      placeholder="e.g., Snackbar De Hoek, 2 min walk, open till 22:00"
                      maxLength={200}
                      className="h-12 bg-secondary border-border"
                      data-testid="nearby-note-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nearby-place" className="text-foreground">
                        Place <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="nearby-place"
                        value={facilities.nearbyPlace}
                        onChange={(e) => setFacility('nearbyPlace')(e.target.value)}
                        placeholder="Snackbar De Hoek"
                        maxLength={80}
                        className="h-12 bg-secondary border-border"
                        data-testid="nearby-place-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nearby-walk" className="text-foreground">
                        Walk, Minutes <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="nearby-walk"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={60}
                        value={facilities.nearbyWalkMinutes}
                        onChange={(e) => setFacility('nearbyWalkMinutes')(e.target.value)}
                        placeholder="2"
                        className="h-12 bg-secondary border-border"
                        data-testid="nearby-walk-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nearby-open-until" className="text-foreground">
                        Open Until <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="nearby-open-until"
                        type="time"
                        value={facilities.nearbyOpenUntil}
                        onChange={(e) => setFacility('nearbyOpenUntil')(e.target.value)}
                        className="h-12 bg-secondary border-border"
                        data-testid="nearby-open-until-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* A serving time only for the venue's own meal; diet for any food the talent can
                  get, and for food nearby it is what the place offers as far as the venue knows. */}
              {['at_venue', 'nearby'].includes(facilities.foodSource) && (
                <div className={cn('grid grid-cols-1 gap-4', facilities.foodSource === 'at_venue' ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
                  {facilities.foodSource === 'at_venue' && (
                    <div className="space-y-2">
                      <Label htmlFor="served-at" className="text-foreground">Served At</Label>
                      <Input
                        id="served-at"
                        type="time"
                        value={facilities.servedAt}
                        onChange={(e) => setFacility('servedAt')(e.target.value)}
                        className="h-12 bg-secondary border-border"
                        data-testid="served-at-input"
                      />
                    </div>
                  )}
                  <FacilitySelect
                    id="vegetarian"
                    label={facilities.foodSource === 'nearby' ? 'Vegetarian options nearby' : 'Vegetarian'}
                    value={facilities.vegetarian}
                    onChange={setFacility('vegetarian')}
                    options={DIET_OPTIONS}
                  />
                  <FacilitySelect
                    id="vegan"
                    label={facilities.foodSource === 'nearby' ? 'Vegan options nearby' : 'Vegan'}
                    value={facilities.vegan}
                    onChange={setFacility('vegan')}
                    options={DIET_OPTIONS}
                  />
                </div>
              )}
            </div>

            {/* Rooms */}
            <div className="space-y-4">
              <p className="micro-label">Rooms</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FacilitySelect id="dressing-room" label="Dressing room" value={facilities.dressingRoom} onChange={setFacility('dressingRoom')} options={DRESSING_ROOM_OPTIONS} />
                <FacilitySelect id="green-room" label="Green room" value={facilities.greenRoom} onChange={setFacility('greenRoom')} options={GREEN_ROOM_OPTIONS} />
                <FacilitySelect id="artist-toilet" label="Artist toilet" value={facilities.artistToilet} onChange={setFacility('artistToilet')} options={ARTIST_TOILET_OPTIONS} />
                <FacilitySelect id="shower" label="Shower" value={facilities.showerAccess} onChange={setFacility('showerAccess')} options={SHOWER_OPTIONS} />
              </div>
            </div>

            {/* Gear & guests */}
            <div className="space-y-4">
              <p className="micro-label">Gear &amp; guests</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FacilitySelect id="secure-storage" label="Secure storage" value={facilities.secureStorage} onChange={setFacility('secureStorage')} options={STORAGE_OPTIONS} />
                <FacilitySelect id="wifi" label="WiFi" value={facilities.wifi} onChange={setFacility('wifi')} options={WIFI_OPTIONS} />
                <FacilitySelect id="drink-tokens" label="Drink tokens" value={facilities.drinkTokens} onChange={setFacility('drinkTokens')} options={DRINK_TOKEN_OPTIONS} />
                <div className="space-y-2">
                  <Label htmlFor="guest-list" className="text-foreground">Guest List Spots</Label>
                  <Input
                    id="guest-list"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={50}
                    value={facilities.guestListSpots}
                    onChange={(e) => setFacility('guestListSpots')(e.target.value)}
                    placeholder="0"
                    className="h-12 bg-secondary border-border"
                    data-testid="guest-list-input"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="facility-notes" className="text-foreground">Anything Else</Label>
              <Textarea
                id="facility-notes"
                value={facilities.notes}
                onChange={(e) => setFacility('notes')(e.target.value)}
                placeholder="e.g., Mirror and iron in the dressing room"
                maxLength={300}
                rows={2}
                className="bg-secondary border-border"
                data-testid="facility-notes-input"
              />
              <p className="text-xs text-muted-foreground">{facilities.notes.length}/300</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riders Section */}
      <div className="mt-8">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-primary" />
              Venue Technical Rider
            </CardTitle>
            <p className="text-sm text-muted-foreground">Upload your venue's technical rider, stage specs, available equipment list, or house PA details. Artists and crew will see these on your gig listings.</p>
          </CardHeader>
          <CardContent>
            <RiderUpload profileId={user?.profileId} editable />
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Speaker className="h-5 w-5 text-primary" />
              Equipment
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              The gear you own, listed per room. This is what a musician&apos;s rider is
              checked against — a document can&apos;t be compared, a list can.
            </p>
          </CardHeader>
          <CardContent>
            <Link to="/venues/me/inventory" data-testid="backline-link">
              <Button variant="outline" className="gap-2">
                <Speaker className="h-4 w-4" />
                Manage equipment
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="mt-8 pb-8">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="save-profile-btn"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
    </div>
  );
}
